// Meta (Facebook) pixel helpers. The pixel BASE snippet lives in index.html and
// only loads fbevents.js once a real META_PIXEL_ID is pasted in - while the id
// is empty `window.fbq` is never defined and every helper below silently no-ops.
// Same "no key = inert" posture as `trackWeb` in analytics.ts: analytics must
// never throw into the page.

type FbqParams = Record<string, string | number | boolean>

interface FbqOptions {
  eventID: string
}

type Fbq = (
  command: 'track' | 'trackCustom' | 'init',
  eventName: string,
  params?: FbqParams,
  options?: FbqOptions,
) => void

declare global {
  interface Window {
    fbq?: Fbq
  }
}

// First path segments the pixel must never be called on: their paths ARE
// credentials, and fbevents.js sends the full page URL with every event. No SPA
// route lives under them today (Pages Functions serve all three), but this shell
// is the fallback for any path a function does not claim. Mirror of the
// denylist in index.html and public/analytics.js, which cannot import this;
// functions/__tests__/meta-store-click.test.ts runs one table through them all.
const META_PIXEL_DENIED_PAGES = new Set(['trip', 'invite', 'place'])

export function metaPixelAllowed(pathname: string): boolean {
  const segment = pathname.split('/').filter(Boolean)[0] ?? ''
  return !META_PIXEL_DENIED_PAGES.has(segment.toLowerCase())
}

function getFbq(): Fbq | undefined {
  if (typeof window === 'undefined') return undefined
  if (!metaPixelAllowed(window.location.pathname)) return undefined
  return typeof window.fbq === 'function' ? window.fbq : undefined
}

/**
 * A deduplication id shared by the browser pixel event and (later) its
 * server-side Conversions API twin, so Meta counts one conversion, not two.
 */
export function newEventId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // fall through to the non-crypto id below
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

/** Diagnostic store click. This is neither a lead nor a verified install. */
export function trackMetaStoreClick(
  props: { store: string; placement: string },
  eventId: string,
): void {
  const fbq = getFbq()
  if (!fbq) return
  try {
    fbq(
      'trackCustom',
      'AppStoreClick',
      { content_name: 'app_download', ...props },
      { eventID: eventId },
    )
  } catch {
    // swallow - analytics must never break the page
  }
}

/** Manual `PageView` for SPA route changes (index.html fires the initial one). */
export function trackMetaPageView(): void {
  const fbq = getFbq()
  if (!fbq) return
  try {
    fbq('track', 'PageView')
  } catch {
    // swallow - analytics must never break the page
  }
}
